import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';

import {
  AnyEntity,
  ButtonModule,
  DropdownComponent,
  DropdownModule,
  IconModule,
  SearchInputModule,
  SimpleInputModule,
  StrNum,
} from '@hglobal/fin-ui';
import { FormControl } from '@ngneat/reactive-forms';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, tap } from 'rxjs';

import { ProviderIconComponent } from '../provider-icon/provider-icon.component';

@Component({
  imports: [
    NgTemplateOutlet,
    DropdownModule,
    SimpleInputModule,
    SearchInputModule,
    IconModule,
    TranslateModule,
    AsyncPipe,
    ReactiveFormsModule,
    ButtonModule,
    ProviderIconComponent,
  ],
  selector: 'kr-search-select',
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchSelectComponent implements OnInit {
  #destroyRef = inject(DestroyRef);

  #value: StrNum[] = [];

  control = input<FormControl<StrNum>>();
  items = input<AnyEntity[]>();
  placeholder = input<string>();
  label = input<string>();
  hint = input<string>();
  withSearch = input<boolean>(true);
  required = input<boolean>();
  nameField = input('name');
  valueField = input('id');
  height = input(44);
  searchByFields = input<string[]>([]);
  multi = input(false);
  isReadonly = input(false);

  itemTpl = contentChild<TemplateRef<any>>('itemTpl');

  searchControl = new FormControl<string>('');
  filteredItems = signal<AnyEntity[]>([]);

  dropdown = viewChild<DropdownComponent>('dropdown');

  computedSearchFields = computed(() => {
    return [this.nameField(), ...this.searchByFields()];
  });

  itemsEffect = effect(() => {
    const items = this.items();

    untracked(() => {
      this.filteredItems.set(items);
      this.searchControl.setValue('');
    });
  });

  get selectedValue(): AnyEntity | string {
    const selectedItems = this.#value.map(val => this.items().find(item => item[this.valueField()] === val));

    return this.multi() ? selectedItems.map(item => item[this.nameField()]).join(', ') : selectedItems[0];
  }

  ngOnInit(): void {
    this.#initializeControl();
    this.#initializeSearchControl();
  }

  filterItems(search: string): void {
    if (!search) {
      this.filteredItems.set(this.items());
      return;
    }

    const filtered = this.items().filter(item =>
      this.computedSearchFields().some(field => item[field]?.toString().toLowerCase().includes(search.toLowerCase())),
    );
    this.filteredItems.set(filtered);
  }

  toggleItem(item: AnyEntity, event: Event) {
    const multi = this.multi();
    if (multi) {
      event.stopPropagation();
    }

    const itemValue = item[this.valueField()];

    const value = multi ? (this.isSelected(item) ? this.#value.filter(val => val !== itemValue) : [...this.#value, itemValue]) : itemValue;

    this.control().setValue(multi ? value.join(',') : value);
  }

  isSelected(item: AnyEntity): boolean {
    return this.#value.includes(item[this.valueField()]);
  }

  #initializeControl(): void {
    const control = this.control();
    this.#setInitialValue(control.value);
    control.valueChanges
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap(() => this.dropdown().close()),
        tap(value => this.#setInitialValue(value)),
      )
      .subscribe();
  }

  #initializeSearchControl(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.#destroyRef))
      .subscribe(searchTerm => this.filterItems(searchTerm));
  }

  #setInitialValue(value: StrNum): void {
    if (!value) {
      this.#value = [];
      return;
    }

    if (this.multi() && typeof value === 'string') {
      this.#value = value.split(',').map(item => +item || item); // to number if id type number
      return;
    }

    this.#value = [value];
  }
}
